/**
 * Offboarding Banner
 * 퇴사 관련 상단 한 줄 배너 — 사이드바 상시 메뉴를 없앤 대신, "내 행동이 필요할 때만" 안내한다.
 *
 * 두 가지 케이스를 커버:
 * 1) 당사자: 내 퇴사 케이스가 진행중(in_progress, 관리자 등록 포함 → 인수인계 작성 필요)
 *    또는 반려(rejected → 확인·재제출 필요)면 배너 표시.
 * 2) 인수자: 확인 대기(awaiting_confirm) 인수인계 항목이 있으면 배너 표시.
 *
 * 알림/푸시(_notify_employee/_notify_receiver)는 별도 발송되며, 이 배너는 상시 진입점 역할.
 * 링크는 always-allowed 라우트(/offboarding/my)로 연결된다.
 */

import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { DoorOpen, Inbox, X } from "lucide-react";
import { useState } from "react";
import { offboardingService } from "@/services/offboardingService";
import { useAuthStore } from "@/stores/authStore";

export default function OffboardingBanner() {
  const { user } = useAuthStore();
  const location = useLocation();
  const isEmployee = user?.user_type === "employee";
  const [myDismissed, setMyDismissed] = useState(false);
  const [receivingDismissed, setReceivingDismissed] = useState(false);

  // 퇴사 페이지 안에서는 배너가 중복 안내가 되므로 숨김
  const onOffboardingPage = location.pathname.startsWith("/offboarding/my");

  // 1) 내 퇴사 케이스 (당사자) — MyOffboardingPage 와 같은 쿼리키로 캐시 공유
  const { data: myCaseData } = useQuery({
    queryKey: ["offboarding", "my-case"],
    queryFn: () => offboardingService.getMyCase(),
    enabled: isEmployee,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
  const myCase = myCaseData?.case ?? null;
  // 내 행동이 필요한 상태만: 진행중(인수인계 작성·절차 진행) / 반려(확인·수정)
  const myCaseActive = myCase?.status === "in_progress";
  const myCaseRejected = myCase?.status === "rejected";

  // 2) 받은 인수인계 (인수자)
  const { data: receivingData } = useQuery({
    queryKey: ["handover-receiving-banner"],
    queryFn: () => offboardingService.listReceivingCases(),
    enabled: isEmployee,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const totalAwaiting = (receivingData?.items ?? []).reduce(
    (sum, c) => sum + (c.awaiting_confirm || 0),
    0
  );

  if (onOffboardingPage) return null;

  const showMy = !myDismissed && (myCaseActive || myCaseRejected);
  const showReceiving = !receivingDismissed && totalAwaiting > 0;

  if (!showMy && !showReceiving) return null;

  return (
    <>
      {/* 당사자: 내 퇴사 절차 진행/반려 */}
      {showMy && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
          <div className="flex items-center justify-between max-w-screen-xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <DoorOpen className="w-4 h-4 shrink-0" />
              <span>
                {myCaseRejected ? (
                  <>
                    제출하신 사직서가 <strong>반려</strong>되었습니다. 내용을 확인해주세요.
                  </>
                ) : (
                  <>
                    퇴사 절차가 <strong>진행 중</strong>입니다. 인수인계 작성과 진행 상황을
                    확인해주세요.
                  </>
                )}
              </span>
              <Link
                to="/offboarding/my"
                className="underline font-medium hover:text-amber-900"
              >
                확인하기
              </Link>
            </div>
            <button
              onClick={() => setMyDismissed(true)}
              className="text-amber-400 hover:text-amber-600 p-1"
              aria-label="배너 닫기"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* 인수자: 확인할 인수인계 항목 */}
      {showReceiving && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
          <div className="flex items-center justify-between max-w-screen-xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <Inbox className="w-4 h-4 shrink-0" />
              <span>
                확인할 인수인계 항목이 <strong>{totalAwaiting}건</strong> 있습니다.
              </span>
              <Link
                to="/offboarding/my?tab=receiving"
                className="underline font-medium hover:text-amber-900"
              >
                확인하기
              </Link>
            </div>
            <button
              onClick={() => setReceivingDismissed(true)}
              className="text-amber-400 hover:text-amber-600 p-1"
              aria-label="배너 닫기"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
